import React, { useContext, useMemo, useState } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import { GenderMale, GenderFemale, GenderAmbiguous, HeartFill, ChatFill, Calendar } from 'react-bootstrap-icons';
import { useTranslation } from '../../i18n';
import { AppContext } from '../../window';

interface QuizFlowProps {
    onComplete: (answers: QuizAnswers) => void;
    onFirstAnswer?: () => void;
}

interface QuizAnswers {
    gender: string;
    seeking: string;
    ageRange: string;
    intent: string;
}

const QuizFlow: React.FC<QuizFlowProps> = ({ onComplete, onFirstAnswer }) => {
    const { t } = useTranslation();
    const { whitelabel, data } = useContext(AppContext);
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Partial<QuizAnswers>>({});

    const seekingOptions = useMemo(() => {
        const options = [];

        if (whitelabel.wantedGender === 'shemale') {
            const shemaleAttr = data.attributes.find(attr => attr.name === 'gender' && attr.value === 'Shemale');
            const label = shemaleAttr?.translatedValue ?? 'Trans';
            options.push({ value: 'shemale', label, icon: <GenderAmbiguous /> });
        }

        options.push(
            { value: 'women', label: t('landing.quiz_women'), icon: <GenderFemale /> },
            { value: 'men', label: t('landing.quiz_men'), icon: <GenderMale /> },
            { value: 'everyone', label: t('landing.quiz_everyone'), icon: <HeartFill /> },
        );

        return options;
    }, [whitelabel.wantedGender, data.attributes, t]);

    const questions = [
        {
            id: 'gender',
            question: t('landing.quiz_i_am'),
            options: [
                { value: 'male', label: t('landing.quiz_man'), icon: <GenderMale /> },
                { value: 'female', label: t('landing.quiz_woman'), icon: <GenderFemale /> }
            ]
        },
        {
            id: 'seeking',
            question: t('landing.quiz_looking_for'),
            options: seekingOptions
        },
        {
            id: 'ageRange',
            question: t('landing.quiz_my_age'),
            options: [
                { value: '18-24', label: t('landing.quiz_age_18_24'), icon: <Calendar /> },
                { value: '25-34', label: t('landing.quiz_age_25_34'), icon: <Calendar /> },
                { value: '35-44', label: t('landing.quiz_age_35_44'), icon: <Calendar /> },
                { value: '45+', label: t('landing.quiz_age_45_plus'), icon: <Calendar /> }
            ]
        },
        {
            id: 'intent',
            question: t('landing.quiz_i_want_to'),
            options: [
                { value: 'chat', label: t('landing.quiz_chat_flirt'), icon: <ChatFill /> },
                { value: 'date', label: t('landing.quiz_casual_dating'), icon: <HeartFill /> },
                { value: 'relationship', label: t('landing.quiz_find_love'), icon: <HeartFill /> }
            ]
        }
    ];

    const handleAnswer = (value: string) => {
        const question = questions[currentStep];
        const newAnswers = { ...answers, [question.id]: value };
        setAnswers(newAnswers);

        if (currentStep === 0) {
            onFirstAnswer?.();
        }

        if (currentStep < questions.length - 1) {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 300);
        } else {
            onComplete(newAnswers as QuizAnswers);
        }
    };

    const progress = ((currentStep + 1) / questions.length) * 100;
    const currentQuestion = questions[currentStep];

    return (
        <div className="quiz-flow">
            <ProgressBar 
                now={progress} 
                className="mb-4" 
                style={{ height: '8px' }}
                variant={currentStep === 0 ? 'primary' : currentStep === 1 ? 'success' : currentStep === 2 ? 'info' : 'danger'}
                animated
                striped
            />
            
            <div className="quiz-question text-center mb-4">
                <h3 className="mb-4">{currentQuestion.question}</h3>
                
                <div className="quiz-options">
                    {currentQuestion.options.map((option, index) => {
                        // Use different outline button variants for visual appeal
                        const variants = ['outline-primary', 'outline-success', 'outline-danger', 'outline-info'];
                        const variant = currentStep === 0 ? (index === 0 ? 'outline-primary' : 'outline-danger') // Male/Female
                                      : currentStep === 1 ? variants[index] || 'outline-primary' // Looking for
                                      : currentStep === 2 ? 'outline-warning' // Age ranges
                                      : currentStep === 3 ? (index === 0 ? 'outline-info' : index === 1 ? 'outline-warning' : 'outline-danger') // Intent
                                      : 'outline-primary';
                        
                        return (
                            <Button
                                key={option.value}
                                variant={variant}
                                size="lg"
                                className="quiz-option d-flex align-items-center justify-content-center mb-3 w-100 py-3 position-relative overflow-hidden"
                                onClick={() => handleAnswer(option.value)}
                                style={{
                                    transition: 'all 0.3s ease',
                                    transform: 'scale(1)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                    e.currentTarget.classList.add('shadow-sm');
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.classList.remove('shadow-sm');
                                }}
                            >
                                <span className="me-2 d-flex align-items-center" style={{ fontSize: '1.5rem' }}>
                                    {option.icon}
                                </span>
                                <span className="fw-bold">{option.label}</span>
                            </Button>
                        );
                    })}
                </div>
            </div>
            
            <div className="quiz-progress text-center">
                <small className="text-muted">
                    {t('landing.quiz_step_of', { current: currentStep + 1, total: questions.length })}
                </small>
            </div>
        </div>
    );
};

export default QuizFlow;